import React, {
  useCallback,
  useEffect,
  useState,
  useContext,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import MultiSelectDropdown from "../../../utils/components/common/multiSelectDropdown/MultiSelectDropdown";
import DateCalendar from "../../../utils/components/common/date_time_picker/DateCalendar";
import { UserContext } from "../../../layouts/MainLayout";
import SearchBox from "../../../utils/components/common/searchBox/deviceSearchBox";
import { getSiteByTenantId } from "../../../services/SitesService";
import "../ReportDetails.scss";

const AuditTrailFilter = ({
  updateFilterValues,
  updateDurationErr,
  currentReportType,
}) => {
  const { t } = useTranslation("reports");

  const [durationErr, setDurationErr] = useState(false);
  const [siteDetails, setSiteDetails] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [isAllSites, setIsAllSites] = useState(false);

  const searchBoxRef = useRef();

  const { userData, featureEnabledStatus } = useContext(UserContext);

  const getSiteDetails = useCallback(async () => {
    const queryParams = {
      tenantId: userData.OrganizationID,
      siteStatus: "All",
      sort: [
        {
          SiteName: "ASC",
        },
      ],
    };
    getSiteByTenantId(queryParams)
      .then((response) => {
        setSiteDetails(response.data?.SiteList);
      })
      .catch((error) => {});
  }, [userData]);

  useEffect(() => {
    getSiteDetails();
  }, [getSiteDetails, userData]);

  useEffect(() => {
    const siteArray = [];

    if (siteDetails.length > 0) {
      siteDetails.map((item) => {
        const { SiteName, SiteId } = item;
        siteArray.push({ name: SiteName, id: SiteId });
      });
    }

    setSiteList(siteArray);
  }, [siteDetails]);

  /**Dropdown default values starts */
  const actionTypeOption = useMemo(() => {
    const actionTypeList = [
      { name: t("reportFilter.auditTrail.userSession"), id: "UserSession" },
      {
        name: t("reportFilter.auditTrail.deviceUpdates"),
        id: "DeviceUpdates",
      },
      { name: t("reportFilter.auditTrail.deviceSetup"), id: "DeviceSetup" },
      {
        name: t("reportFilter.auditTrail.userManagement"),
        id: "UserManagement",
      },
      {
        name: t("reportFilter.auditTrail.profileUpdates"),
        id: "ProfileUpdates",
      },
      {
        name: t("reportFilter.auditTrail.locationManagement"),
        id: "LocationManagement",
      },
      {
        name: t("reportFilter.auditTrail.userActionTypeEnums.DeviceWiFiSettings"),
        id: "DeviceWiFiSettings",
      }
    ];
    if(featureEnabledStatus?.scheduler){
      actionTypeList.push({
        name: t("reportFilter.auditTrail.userActionTypeEnums.Scheduler"),
        id: "Scheduler",
      })
    }
    return actionTypeList;
  }, [t]);

  const [userVal, setUserVal] = useState([]);
  const [deviceVal, setDeviceVal] = useState([]);
  const [actionTypeVal, setActionTypeVal] = useState([]);
  const [siteVal, setSiteVal] = useState([]);

  const [timestamp, setTimestamp] = useState({
    between: [
      new Date().toISOString(),
      new Date(new Date() - 1000 * 60 * 60).toISOString(),
    ],
  });
  /**Dropdown default values ends */

  /**OnValue change callbacks for dropdowns starts */

  /**
   * actionType: String [select | remove]
   * filterList: Array<Objects> [Updated list after select or remove]
   * itemUpdated: Object<{id:"", name:""}>  [Item select or removed]
   */

  const updateUserVal = useCallback((filterList) => {
    const userTypeIds = filterList.map((data) => data.PK.slice(2));
    setUserVal(userTypeIds);
  }, []);

  const updateDeviceVal = useCallback((filterList) => {
    const deviceTypeIds = filterList.map(
      (data) => 
      (data?.PK).slice((2))
    );
    setDeviceVal(deviceTypeIds);
  }, []);

  const updateActionTypeVal = useCallback((filterList) => {
    const actionTypeIds = filterList.map((data) => data.id);
    setActionTypeVal(actionTypeIds);
  }, []);

  const updateSiteVal = useCallback((filterList) => {
    const siteId = filterList.map((data) => data.id);
    setSiteVal(siteId);
  }, []);

  const updateTimeStamp = useCallback((time) => {
    const newTimeStamp = time;

    const timestamp1 = new Date(newTimeStamp[0]);
    const timestamp2 = new Date(newTimeStamp[1]);

    if (timestamp1 < timestamp2) {

      setTimestamp((prevVal) => {
        if (newTimeStamp.length) {
          return { between: [...newTimeStamp] };
        }
        return prevVal;
      });
    }
  }, []);
  /**OnValue change callbacks for dropdowns ends */

  /**Updated filter values to pass to parent component starts */
  useEffect(() => {
    const filterObj = {
      timeStamp: timestamp,
      durationErr: durationErr,
    };
    if (actionTypeVal.length) {
      filterObj.userActionTypes = actionTypeVal;
    }
    if (siteVal.length) {
      filterObj.sites = siteVal;
    }

    if (deviceVal.length) {
      filterObj.devices = deviceVal;
    }

    if (userVal.length) {
      filterObj.users = userVal;
    }

    updateFilterValues(filterObj);

  }, [
    actionTypeVal,
    updateFilterValues,
    deviceVal,
    userVal,
    timestamp,
    durationErr,
    siteVal,
  ]);

  return (
    <div className="audit-trail-filter">
      <div className="left-container">
        <div className={isAllSites ? "dropdown isAllSites" : "dropdown"}>
          <SearchBox
            ref={searchBoxRef}
            autoid="user-search-device"
            getOptionCallBack={updateUserVal}
            isMultiSelect={currentReportType === "auditTrail"}
            isFilter={true}
            isUserSearch={true}
            placeholderText={t("user_placeholder")}
            titleText={t("user")}
          />
        </div>

        <div className={isAllSites ? "dropdown isAllSites" : "dropdown"}>
          <MultiSelectDropdown
            dropdownOptions={actionTypeOption}
            placeholder={t("reportFilter.auditTrail.userActionType")}
            showCheckbox={true}
            className="actionType-filter"
            onValSelect={(selectedList, selectedItem) => {
              updateActionTypeVal(selectedList);
            }}
            onValRemove={(selectedList, removedItem) => {
              updateActionTypeVal(selectedList);
            }}
          />
        </div>
        {isAllSites && (
          <div className="dropdown isAllSites siteDropdown">
            <MultiSelectDropdown
              dropdownOptions={siteList}
              placeholder={t("reportFilter.auditTrail.site")}
              showCheckbox={true}
              className="site-filter"
              onValSelect={(selectedList, selectedItem) => {
                updateSiteVal(selectedList);
              }}
              onValRemove={(selectedList, removedItem) => {
                updateSiteVal(selectedList);
              }}
            />
          </div>
        )}
        <div className="dropdown">
          <SearchBox
            ref={searchBoxRef}
            autoid="report-search-device"
            getOptionCallBack={updateDeviceVal}
            isMultiSelect={currentReportType === "auditTrail"}
            isFilter={true}
            isUserSearch={false}
            placeholderText={t(
              "reportFilter.auditTrail.deviceSearch.placeholder"
            )}
            titleText={t("reportFilter.auditTrail.deviceSearch.title")}
            isSpecificFilter="deviceFilter"
          />
        </div>
      </div>
      <div>
        <DateCalendar
          getSelectedDate={(
            timeStampRange,
            isCustomChecker,
            isErrorActive,
            errorMessage
          ) => {
            updateDurationErr({ isErr: isErrorActive, errMsg: errorMessage });
            setDurationErr(isErrorActive);
            updateTimeStamp(timeStampRange);
          }}
        />
      </div>
    </div>
  );
};

export default AuditTrailFilter;
